import { CommonModule } from "@angular/common";
import { Component } from "@angular/core";
import { SkeletonModule } from "primeng/skeleton";

/**
 * Skeleton loader for Payment Integration Upsert page
 * Matches the layout of payment-integration-upsert.component.html
 */
@Component({
  selector: "app-payment-integration-upsert-skeleton",
  standalone: true,
  imports: [CommonModule, SkeletonModule],
  template: `
    <div class="upsert-skeleton">
      <!-- Page Header Skeleton -->
      <div class="page-header-skeleton">
        <div class="header-row-skeleton">
          <div class="header-left-skeleton">
            <p-skeleton width="2rem" height="2rem" borderRadius="50%" />
            <div class="title-section-skeleton">
              <div class="title-row-skeleton">
                <p-skeleton width="8rem" height="1.125rem" />
                <p-skeleton width="5rem" height="1rem" borderRadius="0.25rem" />
              </div>
              <p-skeleton width="4rem" height="0.75rem" />
            </div>
          </div>
          <div class="header-actions-skeleton">
            <p-skeleton width="4.5rem" height="2rem" borderRadius="4px" />
            <p-skeleton width="4rem" height="2rem" borderRadius="4px" />
          </div>
        </div>
      </div>

      <!-- Form Content Skeleton -->
      <div class="form-content-skeleton">
        <!-- Integration Details Section -->
        <div class="section-skeleton">
          <div class="section-header-skeleton">
            <p-skeleton width="1rem" height="1rem" />
            <p-skeleton width="8rem" height="0.875rem" />
          </div>
          <div class="section-body-skeleton">
            <div class="form-grid-skeleton">
              <!-- Payment Method Type -->
              <div class="field-skeleton full-width">
                <p-skeleton width="9rem" height="0.625rem" />
                <p-skeleton width="100%" height="2.5rem" />
              </div>

              <!-- API Key 1 -->
              <div class="field-skeleton">
                <p-skeleton width="5rem" height="0.625rem" />
                <p-skeleton width="100%" height="2.5rem" />
              </div>

              <!-- API Key 2 -->
              <div class="field-skeleton">
                <p-skeleton width="6rem" height="0.625rem" />
                <p-skeleton width="100%" height="2.5rem" />
              </div>

              <!-- Gateway Payment Type -->
              <div class="field-skeleton full-width">
                <p-skeleton width="9rem" height="0.625rem" />
                <p-skeleton width="100%" height="2.5rem" />
              </div>

              <!-- Checkboxes -->
              <div class="checkbox-row-skeleton full-width">
                <div class="checkbox-skeleton">
                  <p-skeleton
                    width="1.25rem"
                    height="1.25rem"
                    borderRadius="4px"
                  />
                  <p-skeleton width="5rem" height="0.875rem" />
                </div>
                <div class="checkbox-skeleton">
                  <p-skeleton
                    width="1.25rem"
                    height="1.25rem"
                    borderRadius="4px"
                  />
                  <p-skeleton width="6rem" height="0.875rem" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [
    `
      :host {
        display: flex;
        flex-direction: column;
        height: 100%;
        width: 100%;
        overflow: hidden;
        min-height: 0;
      }

      .upsert-skeleton {
        display: flex;
        flex-direction: column;
        height: 100%;
        width: 100%;
        background-color: white;
        overflow: hidden;
      }

      .page-header-skeleton {
        display: flex;
        flex-direction: column;
        padding: 0.5rem 0.75rem;
        background: white;
        border-bottom: 1px solid #e5e7eb;
        flex-shrink: 0;
      }

      .header-row-skeleton {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 0.75rem;
      }

      .header-left-skeleton {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        flex: 1;
        min-width: 0;
      }

      .title-section-skeleton {
        display: flex;
        flex-direction: column;
        gap: 0.125rem;
      }

      .title-row-skeleton {
        display: flex;
        align-items: center;
        gap: 0.75rem;
      }

      .header-actions-skeleton {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        flex-shrink: 0;
      }

      .form-content-skeleton {
        flex: 1;
        overflow-y: auto;
        padding: 0.5rem;
        min-height: 0;
      }

      .section-skeleton {
        background: white;
        border-radius: 0.375rem;
        border: 1px solid #e5e7eb;
        overflow: hidden;
      }

      .section-header-skeleton {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.5rem 0.625rem;
        background: #f9fafb;
        border-bottom: 1px solid #e5e7eb;
      }

      .section-body-skeleton {
        padding: 0.75rem;
      }

      .form-grid-skeleton {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 0.75rem;
        max-width: 800px;
      }

      .field-skeleton {
        display: flex;
        flex-direction: column;
        gap: 0.375rem;
      }

      .field-skeleton.full-width {
        grid-column: 1 / -1;
      }

      .checkbox-row-skeleton {
        display: flex;
        flex-wrap: wrap;
        gap: 1.5rem;
        padding-top: 0.5rem;
      }

      .checkbox-skeleton {
        display: flex;
        align-items: center;
        gap: 0.5rem;
      }

      /* Responsive */
      @media (max-width: 640px) {
        .page-header-skeleton {
          padding: 0.5rem;
        }

        .header-row-skeleton {
          flex-wrap: nowrap;
        }

        .header-actions-skeleton {
          ::ng-deep p-skeleton {
            width: 2rem !important;
          }
        }

        .form-content-skeleton {
          padding: 0.375rem;
        }

        .form-grid-skeleton {
          grid-template-columns: 1fr;
        }

        .checkbox-row-skeleton {
          flex-direction: column;
          gap: 0.75rem;
        }
      }

      /* PrimeNG Skeleton animation override */
      ::ng-deep .p-skeleton {
        background: linear-gradient(
          90deg,
          #f0f0f0 25%,
          #e0e0e0 50%,
          #f0f0f0 75%
        );
        background-size: 200% 100%;
        animation: loading 1.5s infinite;
      }

      @keyframes loading {
        0% {
          background-position: 200% 0;
        }
        100% {
          background-position: -200% 0;
        }
      }
    `,
  ],
})
export class PaymentIntegrationUpsertSkeletonComponent {}
